<template>
  <v-container class="content-explorer-container">
    <ContentExplorerNav
      :isLoading="loading"
      :searchFunc="searchFunc"
      :newContentAvailable="newContentAvailable"
      @refresh-content="getUpdatedContent"
      @view-changed="changeView"
    ></ContentExplorerNav>
    <v-container grid-list-md text-center>
      <v-row
        row
        wrap
        align-content-start
        class="content-container"
        id="alertedContentContainer"
      >
        <v-col v-if="noContent" class="standard col-12">
          Our robots are sorry to report they haven't found anything, but will
          notify you when they do.
        </v-col>
        <v-col v-if="contentLoading" class="standard col-12">
          <Loader text="Hold on while our robots gather your data"></Loader>
        </v-col>
        <v-col
          v-for="(item, index) in contentData"
          :key="index"
          grid-list-xs
          dense
          class="hyp-details-list-item pa-1 my-2"
          :class="[useListView ? 'col-12' : 'col-4']"
          :style="tileStyle"
        >
          <DetailsListItem
            :list-view="useListView"
            :item="item"
            theme="light"
            :show-primary-topics="false"
            :primary-topic="_determinePrimaryTopics(item)"
            :action-icons-enabled="true"
            @read-click="handleReadClick"
            @unread-click="handleUnreadClick"
          ></DetailsListItem>
        </v-col>
        <infinite-loading
          v-if="!noContent"
          @infinite="infiniteHandler"
          :distance="10000"
          :identifier="infiniteLoaderId"
        ></infinite-loading>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
/**
 * @Overview The container for displaying all content tied to a user's alerts.
 */
import lodashFind from "lodash/find";
import lodashGet from "lodash/get";
import lodashOrderBy from "lodash/orderBy";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import { mapGetters } from "vuex";

import {
  GET_USER_ALERTED_CONTENT,
  GET_USER_ALERTED_CONTENT_ACTION,
  GET_SELECTOR_PANEL,
} from "@/store/actions.type";
import {
  MODULE_PREFIX_ALERTS,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_ALERTS_CONTENT,
  MODULE_PREFIX_HOME,
} from "@/store";

import {
  getSources,
  getContent as serviceGetContent,
} from "@/services/alerts/alerts.service.js";
import {
  ALERTED_CONTENT_ACTION_READ,
  ALERTED_CONTENT_ACTION_UNREAD,
} from "@/common/constants/alert.js";

import {
  getPrimaryTopics,
  determinePrimaryTopics,
} from "@/services/events/topics.service";

export default {
  name: "AlertContentExplorer",
  props: {
    currentView: {
      type: Number,
      required: true,
    },
  },
  components: {
    ContentExplorerNav: () =>
      import("@/components/alerts/contentExplorer/ContentExplorerNav"),
    DetailsListItem: () =>
      import("@/components/common/details/DetailsListItem"),
    Loader: () => import("@/components/common/Loader"),
  },
  data() {
    let primaryTopics = [];
    const PRIMARY_TOPICS = getPrimaryTopics();
    for (let i = 0; i < PRIMARY_TOPICS.length; i++) {
      primaryTopics.push(PRIMARY_TOPICS[i].type);
    }
    const SOURCES = getSources();
    return {
      searchByVal: "",
      page: 0,
      pageSize: 50,
      sources: SOURCES,
      alertsFilterCnt: -1,
      sourceFilterCnt: -1,
      timeFilterOld: -1,
      infiniteLoaderId: new Date().getTime(),
      attemptedDataLoad: false,
      useListView: false,
      triggeredId: null,
      autoRefreshInterval: null,
      autoRefreshRateInMinutes: 15,
      newContentAvailable: false,
      topics: getPrimaryTopics(),
      topicFilterCnt: -1,
      veracityFilterCnt: -1,
      acceptablePrimaryTopics: primaryTopics,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      contents: MODULE_PREFIX_ALERTS_CONTENT + "alertedContent",
      loading: MODULE_PREFIX_ALERTS_CONTENT + "loading",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      alerts: MODULE_PREFIX_ALERTS + "alerts",
      firebaseEnabled: MODULE_PREFIX_HOME + "firebaseEnabled",
      alertPushTimestamp: MODULE_PREFIX_ALERTS_CONTENT + "alertPushTimestamp",
    }),
    contentLoading() {
      return this.loading && this.contentData.length === 0;
    },
    noContent() {
      return (
        !this.loading && this.attemptedDataLoad && this.contentData.length === 0
      );
    },
    contentData() {
      if (
        lodashGet(this.contents, "data") &&
        Array.isArray(this.contents.data) &&
        this.contents.data.length
      ) {
        const LEN = this.contents.data.length;
        for (let i = 0; i < LEN; i++) {
          let alert = this.contents.data[i];
          if (
            lodashGet(alert, "properties.formatted.triggeredId") &&
            alert.properties.formatted.triggeredId === this.triggeredId
          ) {
            alert.highlight = true;
          }
        }
      }

      let uniqueContent = [];
      if (this.contents && this.contents.data) {
        this.contents.data.forEach((entry) => {
          let accountedFor = uniqueContent.some((compare) => {
            if (
              lodashGet(entry, "properties.documentId") ===
              lodashGet(compare, "properties.documentId")
            ) {
              return true;
            }
          });
          if (!accountedFor) {
            uniqueContent.push(entry);
          }
        });
      }

      return lodashOrderBy(
        uniqueContent,
        ["properties.formatted.occurred"],
        ["desc"]
      );
    },
    alertFilter() {
      return lodashGet(this.preferences, "alerts.content.alertFilter") || [];
    },
    sourceFilter() {
      return lodashGet(this.preferences, "alerts.content.sourceFilter") || [];
    },
    timeFilter() {
      return lodashGet(this.preferences, "alerts.content.timeFilter");
    },
    topicFilter() {
      return lodashGet(this.preferences, "alerts.content.topicFilter") || [];
    },
    veracityFilter() {
      return lodashGet(this.preferences, "alerts.content.veracityFilter") || [];
    },
    tileStyle() {
      let width = "33%";
      if (this.useListView) {
        width = "100%";
      }
      return {
        "max-width": width,
      };
    },
  },
  mounted() {
    if (!this.firebaseEnabled) {
      this.autoRefreshInterval = setInterval(async () => {
        await this.checkForNewContent();
      }, this.autoRefreshRateInMinutes * 60 * 1000);
    }
  },
  methods: {
    async resetState() {
      // NOTE: According to the docs changing this property resets the state for the infinite scroll plug in
      //       see: https://github.com/PeachScript/vue-infinite-loading/tree/master/docs/api#identifier
      this.infiniteLoaderId = new Date().getTime();
      this.attemptedDataLoad = true;
    },
    async getContent(params, state) {
      if (state) {
        params.$state = state;
      } else {
        await this.resetState();
      }

      params = this.buildParamsForGetContent(params);

      if (lodashGet(this.selectorPanel, "selectedData.alertId")) {
        this.selectorPanel.selectedData = null;
        this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
          this.selectorPanel
        );
      }

      return this.$store.dispatch(
        MODULE_PREFIX_ALERTS_CONTENT + GET_USER_ALERTED_CONTENT,
        params
      );
    },
    async searchFunc(value) {
      this.searchByVal = value;
      await this.getContent({});
    },
    async infiniteHandler($state) {
      this.getContent({ pageSize: this.pageSize }, $state);
    },
    handleReadClick(id) {
      this.$store.dispatch(
        MODULE_PREFIX_ALERTS_CONTENT + GET_USER_ALERTED_CONTENT_ACTION,
        {
          action: ALERTED_CONTENT_ACTION_READ,
          id: id,
        }
      );
    },
    handleUnreadClick(id) {
      this.$store.dispatch(
        MODULE_PREFIX_ALERTS_CONTENT + GET_USER_ALERTED_CONTENT_ACTION,
        {
          action: ALERTED_CONTENT_ACTION_UNREAD,
          id: id,
        }
      );
    },
    changeView(isListView) {
      this.useListView = isListView;
    },
    getUpdatedContent() {
      this.newContentAvailable = false;
      this.getContent({});
    },
    attemptRefresh() {
      let alertedContentContainer = document.querySelector(
        "#alertedContentContainer"
      );
      if (alertedContentContainer && alertedContentContainer.scrollTop < 1) {
        this.getUpdatedContent();
      }
    },
    async checkForNewContent() {
      let params = this.buildParamsForGetContent();
      // no default end date so as to make sure we always get the latest
      params.start = dayjs
        .utc()
        .add(-1, "day")
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ss");
      params.pageOffset = 0;
      params.pageSize = 2;
      params.sortDirection = "desc";
      params.sortField = "@timestamp";

      let mostRecentContentRes = await serviceGetContent(params);
      let potentialNewestTimestamp = lodashGet(
        mostRecentContentRes,
        "data[0].properties['@timestamp']"
      );
      let currentNewestTimestamp = lodashGet(
        this.contents,
        "data[0].properties['@timestamp']"
      );
      if (
        potentialNewestTimestamp &&
        currentNewestTimestamp &&
        potentialNewestTimestamp > currentNewestTimestamp
      ) {
        this.newContentAvailable = true;
      }
      if (potentialNewestTimestamp && !currentNewestTimestamp) {
        this.newContentAvailable = true;
      }
    },
    buildParamsForGetContent(params = {}) {
      params.pageOffset = params.pageOffset || 0;
      params.pageSize = this.pageSize || 25;

      if (this.searchByVal) {
        params.search = this.searchByVal;
      }

      if (this.timeFilter) {
        // no default end date so as to make sure we always get the latest
        const start = dayjs()
          .startOf("day")
          .add(this.timeFilter * -1, "day");
        params.start = params.start || start.format("YYYY-MM-DDTHH:mm:ss");
      }

      let alertsToFilterBy = [];

      // selectedData may contain a specific alertid
      if (lodashGet(this.selectorPanel, "selectedData.alertId")) {
        const ALERT = lodashFind(this.alerts, (obj) => {
          return obj.id === this.selectorPanel.selectedData.alertId;
        });
        if (ALERT) {
          this.triggeredId = lodashGet(
            this.selectorPanel,
            "selectedData.triggeredId"
          );
          alertsToFilterBy.push(ALERT);
          params.sourceFilter = this.sources.map((item) => item.id).toString();
        }
      } else {
        alertsToFilterBy =
          this.alertFilter &&
          Array.isArray(this.alertFilter) &&
          this.alertFilter.length > 0
            ? this.alertFilter
            : this.alerts;

        if (
          this.sourceFilter &&
          Array.isArray(this.sourceFilter) &&
          this.sourceFilter.length > 0
        ) {
          params.sourceFilter = this.sourceFilter
            .map((item) => item.id)
            .toString();
        } else {
          params.sourceFilter = this.sources.map((item) => item.id).toString();
        }
      }

      params.alertsIdFilter = alertsToFilterBy.map((item) => item.id);

      if (
        this.topicFilter &&
        Array.isArray(this.topicFilter) &&
        this.topicFilter.length > 0
      ) {
        params.topicFilter = this.topicFilter.map((topic) =>
          topic.singular.toLowerCase()
        );
      }

      if (
        this.veracityFilter &&
        Array.isArray(this.veracityFilter) &&
        this.veracityFilter.length > 0
      ) {
        params.veracityFilter = this.veracityFilter
          .map((item) => item.name)
          .toString();
      }
      return params;
    },
    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    },
    _determinePrimaryTopics(item) {
      let topicScores = lodashGet(item, "properties.formatted.topic_scores");
      if (!topicScores) {
        topicScores = lodashGet(item, "properties.topic_scores");
      }
      let isTwitter =
        lodashGet(item.properties, "formatted.type") === "twitter";
      return determinePrimaryTopics(
        topicScores,
        isTwitter,
        this.acceptablePrimaryTopics
      );
    },
  },
  beforeDestroy() {
    this.triggeredId = null;
    this.stopAutoRefresh();
  },
  watch: {
    "preferences.alerts.content": {
      async handler() {
        if (
          this.alertsFilterCnt !== this.alertFilter.length ||
          this.sourceFilterCnt !== this.sourceFilter.length ||
          this.timeFilterOld !==
            lodashGet(this.preferences, "alerts.content.timeFilter") ||
          this.topicFilterCnt !== this.topicFilter.length ||
          this.veracityFilterCnt !== this.veracityFilter.length
        ) {
          this.alertsFilterCnt = this.alertFilter.length;
          this.sourceFilterCnt = this.sourceFilter.length;
          this.timeFilterOld = lodashGet(
            this.preferences,
            "alerts.content.timeFilter"
          );
          this.topicFilterCnt = this.topicFilter.length;
          this.veracityFilterCnt = this.veracityFilter.length;
          await this.getContent({});
        }
      },
      deep: true,
    },
    currentView: {
      handler() {
        if (this.currentView === 1) {
          this.getContent({});
        }
      },
    },
    alertPushTimestamp: {
      async handler() {
        await this.checkForNewContent();
      },
    },
    newContentAvailable: {
      handler() {
        if (this.newContentAvailable) {
          this.attemptRefresh();
        }
      },
    },
  },
};
</script>

<style scoped>
.content-explorer-container {
  padding: 5px;
}
.content-container {
  height: 80vh;
  overflow-y: auto;
  align-content: start;
}
.hyp-details-list-item >>> .hyp-details-list-item-style {
  background: white !important;
  margin-bottom: 10px;
  margin-right: 2px;
  border: none !important;
}
.hyp-details-list-item >>> .hyp-details-list-item-flex-wrapper {
  margin-top: -12px;
  margin-bottom: -4px;
}
.hyp-details-list-item >>> .hyp-details-list-item-title {
  color: #000000;
}
.hyp-details-list-item >>> .hyp-details-list-item-title {
  color: #000000;
}
.hyp-details-list-item >>> .hyp-details-list-item-summary {
  color: #000000;
}
.hyp-details-list-item >>> .hyp-details-list-item-source {
  color: #000000;
}
/* These are for the scroll bar in the content container */
::-webkit-scrollbar {
  width: 8px;
  height: 3px;
}
::-webkit-scrollbar-thumb {
  height: 50px;
  background-color: #c8c8c8;
  border-radius: 3px;
}
::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(204, 204, 204, 1);
  border-radius: 3px;
}
.content-container >>> .infinite-loading-container {
  margin: 0 auto;
  margin-top: 4em;
}
.content-container >>> .white--text {
  color: grey !important;
}
.standard {
  font-size: 24px;
}
</style>
